import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  fetchCollectionSignals,
  fetchCollectionTargets,
  fetchCompanyIndicators,
  fetchDemographics,
  fetchPages,
  fetchSavedAnalyses,
  fetchSavedAnalysis,
  fetchZoneHistory,
  searchLocal,
  adLibraryUrl,
  Ad,
  errorMessage,
  type CollectionTarget,
} from './api';
import { categoryLabel } from './categories';
import { estimateSpend, formatSpend } from './spesa';
import PageBack from './PageBack';
import { ScoreArc, ZoneVisual } from './MarketVisuals';
import type { AnalysisSession } from './CompetitorAnalysis';
import { readRequestedCompanyIndicators } from './companyReportAccess';
import { RadarEyeVisual } from './InsightVisuals';
import ReportDossiers from './ReportDossiers';

const numFmt = new Intl.NumberFormat('it-IT');
const dateFmt = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

/**
 * Report competitor stampabile: dal browser → Condividi/Stampa → Salva come PDF.
 * È il deliverable da girare al cliente (Fase 5 "lite" di INTEGRAZIONI.md).
 */
export default function Report() {
  const [params] = useSearchParams();
  return params.get('ids') ? <LegacyMetaReport/> : <LatestAnalysisReport/>;
}

function LatestAnalysisReport() {
  const [view, setView] = useState<ReportView>('summary');
  const analyses = useQuery({ queryKey: ['saved-analyses', 0, 1], queryFn: () => fetchSavedAnalyses(0, 1) });
  const latestId = analyses.data?.content[0]?.id;
  const latest = useQuery({
    queryKey: ['saved-analysis', latestId],
    queryFn: () => fetchSavedAnalysis<AnalysisSession>(latestId!),
    enabled: Boolean(latestId),
  });

  if (view === 'dossiers') return <ReportPageFrame view={view} setView={setView} subtitle="Archivio dei PDF generati"><ReportDossiers/></ReportPageFrame>;
  if (analyses.isPending || (latestId && latest.isPending)) return <ReportPageFrame view={view} setView={setView} subtitle="Preparo l’ultima analisi"><section className="zone-page-loading report-page-state" role="status"><span className="sr-only">Preparazione report</span><i/><i/><i/></section></ReportPageFrame>;
  if (analyses.isError || latest.isError) return <ReportPageFrame view={view} setView={setView} subtitle="Report non disponibile"><section className="empty report-page-state"><h2>Non riesco a preparare il report</h2><p>{errorMessage(analyses.error ?? latest.error)}</p><button className="btn-secondary" type="button" onClick={() => { analyses.refetch(); latest.refetch(); }}>Riprova</button></section></ReportPageFrame>;
  if (!latest.data) return <ReportPageFrame view={view} setView={setView} subtitle="Nessuna analisi salvata"><section className="empty report-empty report-page-state"><h2>Completa prima un’analisi</h2><p>Il report usa sempre l’ultimo panorama salvato, senza ricostruire o inventare dati.</p><Link className="btn-primary" to="/competitor?new=1">Avvia un’analisi</Link></section></ReportPageFrame>;

  const saved = latest.data;
  const session = saved.payload;
  const opportunity = session.opportunity;
  const market = session.market;
  const opportunityCount = opportunity?.opportunities.length
    ?? market?.competitors.filter((item) => item.strongPresence === false && (item.rating ?? 0) >= market.avgRating).length
    ?? 0;
  const growingSectors = opportunity
    ? new Set([...(opportunity.opportunities ?? []).map((item) => item.key), ...(opportunity.importableTrends ?? []).map((item) => item.key)]).size
    : market?.gaps?.missing.length ?? 0;
  const competitionCount = opportunity?.businessesFound ?? market?.count ?? session.classifications.filter((item) => item.label !== 'irrilevante').length;
  const competitionLabel = competitionCount >= 60 ? 'Alta' : competitionCount >= 25 ? 'Moderata' : 'Contenuta';
  const totalReach = session.classifications.reduce((sum, item) => sum + item.totalReach, 0);
  const marketDigitalCount = market?.competitors.filter((item) => item.strongPresence || Boolean(item.website)).length ?? 0;
  const marketDigitalShare = market?.count ? marketDigitalCount / market.count : null;
  const adsLabel = totalReach > 500_000 ? 'Alta' : totalReach > 100_000 ? 'Media' : totalReach > 0 ? 'Bassa'
    : marketDigitalShare != null ? marketDigitalShare >= .6 ? 'Alta' : marketDigitalShare >= .25 ? 'Media' : 'Bassa' : 'Non rilevata';
  const adsDetail = totalReach > 0 ? `Reach pubblica osservata: ${numFmt.format(totalReach)}`
    : marketDigitalShare != null ? `${marketDigitalCount} su ${market?.count ?? 0} con sito o presenza forte` : 'Nessun segnale digitale nell’analisi salvata';
  const score = opportunity
    ? Math.round(Math.max(0, Math.min(1, Math.max(0, ...opportunity.opportunities.map((item) => item.score)))) * 100)
    : Math.min(100, Math.round((competitionCount / Math.max(competitionCount + opportunityCount, 1)) * 70 + (opportunityCount > 0 ? 20 : 0)));
  const verdict = opportunity?.reading || market?.gaps?.reading || saved.summary || 'Il report sintetizza i segnali disponibili nell’ultima analisi.';
  const radius = opportunity?.radiusMeters ?? market?.radiusMeters;

  function printCompleteReport() {
    setView('full');
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => window.print()));
  }

  return <div className="latest-report zone-data-page">
    <header className="report-page-head"><div><p className="eyebrow">Ultima analisi salvata</p><h1>Report</h1><p>{saved.title} · {dateFmt.format(new Date(saved.createdAt))}</p></div><button type="button" className="report-share" onClick={printCompleteReport} aria-label="Stampa o salva il report completo">↗</button></header>
    <ReportTabs view={view} setView={setView}/>

    {radius != null && <ZoneVisual compact radiusLabel={radius >= 1_000 ? `${radius / 1_000} km` : `${radius} m`} title={session.zone || saved.title} markers={Math.min(7, competitionCount)}/>}

    <section className="report-counter-list" aria-label="Indicatori principali">
      <article><span aria-hidden="true">◇</span><div><strong>Attività con più potenziale</strong><small>Segnali da approfondire</small></div><b>{opportunityCount}</b></article>
      <article><span aria-hidden="true">↗</span><div><strong>Settori in crescita</strong><small>Categorie con segnali positivi</small></div><b>{growingSectors}</b></article>
      <article><span aria-hidden="true">△</span><div><strong>Concorrenza media</strong><small>{numberLabel(competitionCount)} pertinenti</small></div><b>{competitionLabel}</b></article>
      <article><span aria-hidden="true">◃</span><div><strong>Intensità pubblicitaria media</strong><small>{adsDetail}</small></div><b>{adsLabel}</b></article>
    </section>

    <section className="report-verdict" aria-labelledby="report-verdict-title"><RadarEyeVisual className="report-verdict-eye"/><div><p className="eyebrow">Verdetto</p><h2 id="report-verdict-title">{score >= 75 ? 'Zona con segnali forti' : score >= 50 ? 'Spazio da approfondire' : 'Quadro ancora incerto'}</h2><p>{verdict}</p></div><ScoreArc value={score} label="Potenziale"/></section>

    {view === 'full' && <ReportFullSections session={session} analysisId={saved.id} savedAt={saved.createdAt} />}

    <div className="report-data-note"><span aria-hidden="true">i</span><p>I valori provengono dall’analisi salvata il {dateFmt.format(new Date(saved.createdAt))}. Gli indicatori sono esplorativi e non prevedono fatturato o successo.</p></div>
    <button type="button" className="btn-primary report-download" onClick={printCompleteReport}>Scarica report completo <span aria-hidden="true">↓</span></button>
  </div>;
}

type ReportView = 'summary' | 'full' | 'dossiers';

function ReportTabs({ view, setView }: { view: ReportView; setView: (view: ReportView) => void }) {
  return <nav className="report-toggle" aria-label="Sezioni report"><button type="button" className={view === 'summary' ? 'is-active' : ''} aria-current={view === 'summary' ? 'page' : undefined} onClick={() => setView('summary')}>Sintesi</button><button type="button" className={view === 'full' ? 'is-active' : ''} aria-current={view === 'full' ? 'page' : undefined} onClick={() => setView('full')}>Completo</button><button type="button" className={view === 'dossiers' ? 'is-active' : ''} aria-current={view === 'dossiers' ? 'page' : undefined} onClick={() => setView('dossiers')}>I tuoi dossier</button></nav>;
}

function ReportPageFrame({ view, setView, subtitle, children }: { view: ReportView; setView: (view: ReportView) => void; subtitle: string; children: ReactNode }) {
  return <div className="latest-report zone-data-page"><header className="report-page-head"><div><p className="eyebrow">Report e dossier</p><h1>Report</h1><p>{subtitle}</p></div></header><ReportTabs view={view} setView={setView}/>{children}</div>;
}

function numberLabel(value: number) {
  return value === 1 ? '1 attività' : `${numFmt.format(value)} attività`;
}

interface ReportFullSectionsProps {
  session: AnalysisSession;
  analysisId: string;
  savedAt: string;
}

function ReportFullSections({ session, analysisId, savedAt }: ReportFullSectionsProps) {
  const targets = useQuery({ queryKey: ['collection-targets'], queryFn: fetchCollectionTargets });
  const center = session.opportunity?.center ?? session.market?.center ?? null;
  const zoneTarget = useMemo(
    () => matchReportTarget(targets.data ?? [], 'zona', session.zone, center),
    [targets.data, session.zone, center],
  );
  const demandTarget = useMemo(
    () => matchReportTarget(targets.data ?? [], 'demand_zone', session.zone, center),
    [targets.data, session.zone, center],
  );
  const history = useQuery({
    queryKey: ['zone-history', zoneTarget?.id, 365],
    queryFn: () => fetchZoneHistory(zoneTarget!.id, 365),
    enabled: Boolean(zoneTarget),
  });
  const signals = useQuery({
    queryKey: ['collection-signals', demandTarget?.id, 0, 50],
    queryFn: () => fetchCollectionSignals(demandTarget!.id, 0, 50),
    enabled: Boolean(demandTarget),
  });
  const requestedCompanies = useMemo(() => {
    const byPage = new Map(session.classifications.map((row) => [row.pageId, row.pageName || row.pageId]));
    return readRequestedCompanyIndicators()
      .filter((row) => byPage.has(row.pageId))
      .map((row) => ({ ...row, name: byPage.get(row.pageId)! }));
  }, [session.classifications]);
  const companyIndicators = useQueries({
    queries: requestedCompanies.map((company) => ({
      queryKey: ['company-report-indicators', company.vat],
      queryFn: () => fetchCompanyIndicators(company.vat),
      retry: false,
    })),
  });

  const opportunity = session.opportunity;
  const market = session.market;
  const registry = opportunity?.registry;
  const rent = opportunity?.rentQuote;
  const googleByCategory = new Map((opportunity?.census ?? []).map((row) => [row.key, row.count]));
  const persistentSignals = [...(signals.data?.content ?? [])]
    .sort((a, b) => b.seenCount - a.seenCount || b.lastSeenAt.localeCompare(a.lastSeenAt))
    .slice(0, 6);
  const timeline = history.data?.censusTimeline ?? [];
  const firstCensus = timeline[0];
  const lastCensus = timeline.at(-1);
  const businessDelta = firstCensus && lastCensus ? lastCensus.businessesFound - firstCensus.businessesFound : null;

  return <div className="report-complete" aria-labelledby="report-full-title">
    <section className="report-full-data report-complete-section report-overview" aria-labelledby="report-full-title">
      <header><div><p className="eyebrow">Report completo</p><h2 id="report-full-title">Quadro dell’analisi</h2></div><span className="report-section-number">01–03</span></header>
      <dl>
        <div><dt>Modalità</dt><dd>{session.mode === 'local' ? 'Attività locale' : 'Business online'}</dd></div>
        <div><dt>Zona o mercato</dt><dd>{session.zone || session.description}</dd></div>
        <div><dt>Data analisi</dt><dd>{dateFmt.format(new Date(savedAt))}</dd></div>
        {(opportunity?.radiusMeters ?? market?.radiusMeters) != null && <div><dt>Raggio osservato</dt><dd>{formatMeters((opportunity?.radiusMeters ?? market?.radiusMeters)!)}</dd></div>}
        {(opportunity?.population ?? market?.population) != null && <div><dt>Popolazione stimata</dt><dd>~{numFmt.format((opportunity?.population ?? market?.population)!)}</dd></div>}
      </dl>
      <Link className="btn-secondary no-print" to={`/competitor?analysis=${encodeURIComponent(analysisId)}`}>Apri l’analisi originale</Link>
    </section>

    <section className="report-complete-section" aria-labelledby="report-competition-title">
      <ReportSectionHeading number="04" eyebrow="Panorama competitivo" title="Chi presidia già la zona" id="report-competition-title" />
      {market?.competitors.length ? <div className="report-table-scroll" tabIndex={0} role="region" aria-label="Competitor osservati"><table className="report-competition-table"><thead><tr><th>Attività</th><th>Categoria</th><th>Distanza</th><th>Reputazione</th><th>Presenza</th></tr></thead><tbody>{market.competitors.slice(0, 12).map((competitor, index) => {
        const badge = competitor.name === market.leader || index === 0 ? 'Leader' : competitor.strongPresence ? 'Competitivo' : 'Emergente';
        return <tr key={`${competitor.name ?? 'attivita'}-${index}`}><th scope="row">{competitor.name || 'Attività senza nome'}</th><td>{competitor.primaryType || 'n/d'}</td><td>{competitor.distanceMeters != null ? formatMeters(competitor.distanceMeters) : 'n/d'}</td><td>{competitor.rating != null ? <>{competitor.rating.toFixed(1)} · {numFmt.format(competitor.reviewsCount ?? 0)} rec.</> : 'n/d'}</td><td><span className={`report-rank is-${badge.toLowerCase()}`}>{badge}</span></td></tr>;
      })}</tbody></table></div> : opportunity?.census.length ? <div className="report-table-scroll" tabIndex={0} role="region" aria-label="Categorie censite"><table className="report-competition-table"><thead><tr><th>Categoria</th><th>Attività</th><th>Densità / 10mila</th><th>Benchmark</th></tr></thead><tbody>{opportunity.census.map((row) => <tr key={row.key}><th scope="row">{row.label}</th><td>{numFmt.format(row.count)}{row.capped ? '+' : ''}</td><td>{row.densityPer10k != null ? row.densityPer10k.toFixed(1) : 'n/d'}</td><td>{row.benchmarkPer10k.toFixed(1)}</td></tr>)}</tbody></table></div> : <ReportEmpty>Il dettaglio competitivo non è presente in questa analisi salvata.</ReportEmpty>}
      {market?.gaps?.reading && <p className="report-insight">{market.gaps.reading}</p>}
    </section>

    <section className="report-complete-section" aria-labelledby="report-gaps-title">
      <ReportSectionHeading number="05" eyebrow="Gap e saturazione" title="Cosa manca, cosa è già affollato" id="report-gaps-title" />
      {opportunity?.opportunities.length ? <ol className="report-opportunity-list">{[...opportunity.opportunities].sort((a, b) => b.score - a.score).slice(0, 8).map((item) => <li key={`${item.kind}-${item.key}`}><span><strong>{item.label}</strong><small>{item.kind === 'gap' ? 'Offerta mancante' : item.kind === 'quality' ? 'Offerta migliorabile' : 'Mercato frammentato'}</small></span><b>{Math.round(Math.max(0, Math.min(1, item.score)) * 100)}</b></li>)}</ol> : market?.gaps && (market.gaps.missing.length || market.gaps.oversupplied.length) ? <div className="report-gap-columns"><div><h3>Spazi rilevati</h3>{market.gaps.missing.length ? <ul>{market.gaps.missing.map((item) => <li key={item}>{item}</li>)}</ul> : <p>Nessun gap netto.</p>}</div><div><h3>Offerta satura</h3>{market.gaps.oversupplied.length ? <ul>{market.gaps.oversupplied.map((item) => <li key={item}>{item}</li>)}</ul> : <p>Nessuna saturazione netta.</p>}</div></div> : <ReportEmpty>L’analisi non contiene segnali sufficienti per distinguere gap e saturazione.</ReportEmpty>}
    </section>

    <section className="report-complete-section" aria-labelledby="report-registry-title">
      <ReportSectionHeading number="06" eyebrow="Tessuto ufficiale" title="Registro Imprese e presenza digitale" id="report-registry-title" />
      {registry ? <><div className="report-metric-strip"><div><span>Imprese registrate</span><strong>{numFmt.format(registry.totalCompanies)}</strong></div><div><span>Nuove in 90 giorni</span><strong>+{numFmt.format(registry.newLast90d)}</strong></div><div><span>Attività Google</span><strong>{numFmt.format(opportunity?.businessesFound ?? 0)}</strong></div></div>{registry.byCategory.length > 0 && <div className="report-table-scroll" tabIndex={0} role="region" aria-label="Registro e Google per categoria"><table><thead><tr><th>Categoria</th><th>Registro</th><th>Google</th><th>Scarto</th></tr></thead><tbody>{registry.byCategory.map((row) => {
        const google = googleByCategory.get(row.key);
        const delta = google == null ? null : row.count - google;
        return <tr key={row.key}><th scope="row">{row.label}</th><td>{numFmt.format(row.count)}</td><td>{google == null ? 'n/d' : numFmt.format(google)}</td><td>{delta == null ? 'n/d' : delta > 0 ? `+${numFmt.format(delta)}` : numFmt.format(delta)}</td></tr>;
      })}</tbody></table></div>}<p className="report-source">Fonte: Registro Imprese. Conteggi aggregati per sede legale; la sede operativa può differire.</p></> : <ReportEmpty>Il censimento ufficiale non era disponibile quando questa analisi è stata salvata.</ReportEmpty>}
    </section>

    <section className="report-complete-section" aria-labelledby="report-history-title">
      <ReportSectionHeading number="07" eyebrow="La zona nel tempo" title="Aperture, sparizioni e crescita osservata" id="report-history-title" />
      {targets.isPending || (zoneTarget && history.isPending) ? <ReportLoading label="Recupero dello storico zona" /> : targets.isError || history.isError ? <ReportEmpty>Lo storico non è disponibile in questo momento.</ReportEmpty> : !zoneTarget ? <ReportEmpty>Questa zona non è ancora seguita nella Raccolta dati. Il report resta valido, ma non può mostrare un’evoluzione temporale.</ReportEmpty> : timeline.length === 0 ? <ReportEmpty>La zona è seguita, ma non ha ancora censimenti sufficienti per costruire una serie storica.</ReportEmpty> : <><div className="report-metric-strip"><div><span>Attività osservate</span><strong>{numFmt.format(lastCensus?.businessesFound ?? 0)}</strong></div><div><span>Variazione periodo</span><strong>{businessDelta == null ? 'n/d' : `${businessDelta >= 0 ? '+' : ''}${numFmt.format(businessDelta)}`}</strong></div><div><span>Nuove rilevate</span><strong>{numFmt.format(history.data?.newBusinesses.length ?? 0)}</strong></div><div><span>Non più rilevate</span><strong>{numFmt.format(history.data?.goneBusinesses.length ?? 0)}</strong></div></div><div className="report-timeline" aria-label="Censimenti della zona">{timeline.slice(-8).map((point) => <div key={point.capturedAt}><i style={{ '--history-height': `${Math.max(18, point.businessesFound / Math.max(...timeline.map((row) => row.businessesFound), 1) * 100)}%` } as React.CSSProperties}/><span>{numFmt.format(point.businessesFound)}</span><time dateTime={point.capturedAt}>{new Intl.DateTimeFormat('it-IT', { month: 'short', year: '2-digit' }).format(new Date(point.capturedAt))}</time></div>)}</div>{history.data && history.data.topGrowing.length > 0 && <div className="report-growing"><h3>Attività in crescita</h3><ol>{history.data.topGrowing.slice(0, 4).map((row) => <li key={row.businessId}><span>{row.name || 'Attività senza nome'}</span><strong>+{numFmt.format(row.reviewsDelta)} recensioni</strong></li>)}</ol></div>}<p className="report-source">Fonte: raccolta storica Osservio su osservazioni Google Places e dati aggregati.</p></>}
    </section>

    <section className="report-complete-section" aria-labelledby="report-rent-title">
      <ReportSectionHeading number="08" eyebrow="Costo di ingresso" title="Quotazioni commerciali della zona" id="report-rent-title" />
      {rent ? <><div className="report-rent-grid"><div><span>Fascia mensile</span><strong>€{formatDecimal(rent.minSqm)}–{formatDecimal(rent.maxSqm)}/m²</strong><small>media €{formatDecimal(rent.medSqm)}/m²</small></div><div><span>Esempio 60 m²</span><strong>~€{numFmt.format(rent.monthlyFor60sqm)}/mese</strong><small>{[rent.zoneName, rent.fascia].filter(Boolean).join(' · ') || 'Zona OMI di riferimento'}</small></div></div><p className="report-source">Fonte: elaborazione Osservio su quotazioni OMI, {rent.year} semestre {rent.semester}. Valori orientativi, non una perizia.</p></> : <ReportEmpty>Le quotazioni OMI non erano disponibili per questa analisi salvata.</ReportEmpty>}
    </section>

    <section className="report-complete-section" aria-labelledby="report-signals-title">
      <ReportSectionHeading number="09" eyebrow="Segnali di bisogno" title="Domande che ricorrono nella zona" id="report-signals-title" />
      {targets.isPending || (demandTarget && signals.isPending) ? <ReportLoading label="Recupero dei segnali di domanda" /> : targets.isError || signals.isError ? <ReportEmpty>I segnali di bisogno non sono disponibili in questo momento.</ReportEmpty> : !demandTarget ? <ReportEmpty>Non c’è una raccolta “demand zone” attiva e coerente con questa area.</ReportEmpty> : persistentSignals.length === 0 ? <ReportEmpty>La raccolta è attiva, ma non ha ancora segnali persistenti da mostrare.</ReportEmpty> : <ol className="report-signal-list">{persistentSignals.map((signal) => <li key={signal.id}><span><strong>{signal.signal}</strong><small>{signal.source} · ultima osservazione {dateFmt.format(new Date(signal.lastSeenAt))}</small></span><b>{signal.seenCount}×</b></li>)}</ol>}
    </section>

    {requestedCompanies.length > 0 && <section className="report-complete-section" aria-labelledby="report-company-title">
      <ReportSectionHeading number="10" eyebrow="Profilo elaborato" title="Indicatori societari richiesti" id="report-company-title" />
      <div className="report-company-list">{requestedCompanies.map((company, index) => {
        const query = companyIndicators[index];
        if (query.isPending) return <ReportLoading key={company.pageId} label={`Recupero indicatori per ${company.name}`} />;
        if (!query.data) return <article className="report-company-card" key={company.pageId}><h3>{company.name}</h3><p>Indicatori elaborati non disponibili.</p></article>;
        const indicator = query.data;
        return <article className="report-company-card" key={company.pageId}><header><h3>{company.name}</h3>{indicator.solidita && <span className={`company-health is-${indicator.solidita}`}>{indicator.soliditaLabel || indicator.solidita}</span>}</header><dl>{indicator.dimensione && <div><dt>Dimensione</dt><dd>{indicator.dimensione}</dd></div>}{indicator.fasciaFatturato && <div><dt>Fascia fatturato</dt><dd>{indicator.fasciaFatturato}</dd></div>}{indicator.organico && <div><dt>Organico</dt><dd>{indicator.organico}</dd></div>}{indicator.tendenza && <div><dt>Tendenza</dt><dd>{indicator.tendenza}</dd></div>}{indicator.anniAttivita != null && <div><dt>Anzianità</dt><dd>{indicator.anniAttivita} anni</dd></div>}{indicator.annoBilancio != null && <div><dt>Anno di riferimento</dt><dd>{indicator.annoBilancio}</dd></div>}</dl><p className="report-source">{indicator.fonte}</p></article>;
      })}</div>
    </section>}

    <section className="report-complete-section report-legal" aria-labelledby="report-legal-title">
      <ReportSectionHeading number="11" eyebrow="Trasparenza" title="Fonti e note legali" id="report-legal-title" />
      <ul><li>Analisi salvata Osservio del {dateFmt.format(new Date(savedAt))}.</li>{(opportunity?.population ?? market?.population) != null && <li>Popolazione: Censimento ISTAT 2021 per raggi ≥1,5 km o stima WorldPop 2020 per raggi più piccoli. Licenze: ISTAT CC BY 4.0 · WorldPop CC BY 4.0.</li>}<li>Attività e reputazione locale: Google Places; nel report sono riportati soltanto fatti minimi e indicatori Osservio.</li>{session.classifications.length > 0 && <li>Pubblicità: Meta Ad Library, archivio pubblico di trasparenza.</li>}{registry && <li>Registro Imprese: soli conteggi aggregati, nessuna impresa singola.</li>}{rent && <li>Immobiliare: elaborazione su quotazioni OMI dell’Agenzia delle Entrate.</li>}{requestedCompanies.length > 0 && <li>Profili societari: esclusivamente fasce e indicatori elaborati restituiti dall’endpoint dedicato; nessun valore di bilancio grezzo.</li>}</ul>
      <p><strong>Documento riservato all’uso interno del richiedente, da non divulgare.</strong> Le informazioni sono esplorative, non costituiscono consulenza finanziaria o una previsione di successo e non sono producibili in giudizio.</p>
    </section>
  </div>;
}

function ReportSectionHeading({ number, eyebrow, title, id }: { number: string; eyebrow: string; title: string; id: string }) {
  return <header className="report-section-heading"><div><p className="eyebrow">{eyebrow}</p><h2 id={id}>{title}</h2></div><span className="report-section-number">{number}</span></header>;
}

function ReportEmpty({ children }: { children: string }) {
  return <p className="report-honest-state">{children}</p>;
}

function ReportLoading({ label }: { label: string }) {
  return <div className="report-inline-loading" role="status"><i/><span>{label}…</span></div>;
}

function formatMeters(value: number) {
  return value >= 1_000 ? `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(value / 1_000)} km` : `${numFmt.format(Math.round(value))} m`;
}

function formatDecimal(value: number) {
  return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(value);
}

function normalizeTargetText(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function targetDistanceMeters(target: CollectionTarget, center: { lat: number; lng: number } | null) {
  if (!center || target.config.lat == null || target.config.lng == null) return null;
  const earthRadius = 6_371_000;
  const toRadians = (value: number) => value * Math.PI / 180;
  const latDelta = toRadians(target.config.lat - center.lat);
  const lngDelta = toRadians(target.config.lng - center.lng);
  const a = Math.sin(latDelta / 2) ** 2
    + Math.cos(toRadians(center.lat)) * Math.cos(toRadians(target.config.lat)) * Math.sin(lngDelta / 2) ** 2;
  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function matchReportTarget(targets: CollectionTarget[], kind: CollectionTarget['kind'], zone: string, center: { lat: number; lng: number } | null) {
  const zoneText = normalizeTargetText(zone);
  const zoneTokens = new Set(zoneText.split(' ').filter((token) => token.length > 2));
  const candidates = targets.filter((target) => target.active && target.kind === kind).map((target) => {
    const candidateText = normalizeTargetText([target.label, target.config.address, target.config.city].filter(Boolean).join(' '));
    const candidateTokens = candidateText.split(' ').filter((token) => token.length > 2);
    const overlap = candidateTokens.filter((token) => zoneTokens.has(token)).length;
    const distance = targetDistanceMeters(target, center);
    const textScore = candidateText && zoneText && (candidateText.includes(zoneText) || zoneText.includes(candidateText)) ? 8 : overlap * 2;
    const distanceScore = distance == null ? 0 : distance <= 1_000 ? 8 : distance <= 5_000 ? 5 : distance <= 15_000 ? 2 : -8;
    return { target, score: textScore + distanceScore, distance };
  }).filter((row) => row.score > 0).sort((a, b) => b.score - a.score || (a.distance ?? Number.MAX_VALUE) - (b.distance ?? Number.MAX_VALUE));
  return candidates[0]?.target ?? null;
}

function LegacyMetaReport() {
  const [params] = useSearchParams();
  const ids = (params.get('ids') ?? '').split(',').filter(Boolean);

  const pages = useQuery({ queryKey: ['pages'], queryFn: fetchPages });

  // Per ogni competitor: i 3 annunci con più reach + demografia
  const topAds = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['report-ads', id],
      queryFn: () => searchLocal({ pageId: id, sort: 'reach' as const, size: 3 }),
    })),
  });
  const demos = useQueries({
    queries: ids.map((id) => ({
      queryKey: ['demographics', id],
      queryFn: () => fetchDemographics(id),
    })),
  });

  if (ids.length === 0) {
    return (
      <section className="empty report-empty">
        <PageBack fallback="/annunci" label="Indietro" />
        <p className="eyebrow">Report</p>
        <h1>Nessuna azienda selezionata</h1>
        <p>Cerca negli Annunci, seleziona una o più aziende dai risultati raggruppati e scegli “Genera report”.</p>
        <Link className="btn-primary" to="/annunci">Vai agli annunci</Link>
      </section>
    );
  }

  if (pages.isPending) return <p className="demo-note">Preparo il report…</p>;

  const rows = (pages.data ?? []).filter((p) => ids.includes(p.pageId));
  const reachLeader = rows.reduce<typeof rows[number] | null>((leader, page) => !leader || page.totalReach > leader.totalReach ? page : leader, null);
  const activeLeader = rows.reduce<typeof rows[number] | null>((leader, page) => !leader || page.activeCount > leader.activeCount ? page : leader, null);
  const totalActive = rows.reduce((sum, page) => sum + page.activeCount, 0);
  const totalAds = rows.reduce((sum, page) => sum + page.adsCount, 0);

  return (
    <div className="report">
      <PageBack fallback="/annunci" label="Torna ai risultati" />
      <header className="report-head">
        <p className="report-brand">
          Osservio<span className="wordmark-dot">.</span> · Report competitor
        </p>
        <h1>Analisi advertising Meta</h1>
        <p className="report-date">
          {dateFmt.format(new Date())} · {rows.length}{' '}
          {rows.length === 1 ? 'competitor analizzato' : 'competitor analizzati'} · dati:
          Meta Ad Library (trasparenza UE) e Google
        </p>
      </header>

      <button type="button" className="btn-primary no-print" onClick={() => window.print()}>
        Stampa / Salva come PDF
      </button>

      {rows.length > 0 && <>
        <section className="report-summary" aria-labelledby="report-summary-title">
          <div><p className="eyebrow">Sintesi esecutiva</p><h2 id="report-summary-title">{reachLeader?.pageName} guida la reach{activeLeader && activeLeader.pageId !== reachLeader?.pageId ? `, ${activeLeader.pageName} l’attività` : ' e l’attività'}</h2><p>{totalAds > 0 ? `${Math.round(totalActive / totalAds * 100)}% degli annunci osservati è attualmente attivo.` : 'Non ci sono annunci attivi nel campione.'} Il confronto separa volume, reach e longevità per evitare di interpretare il solo numero di inserzioni come leadership.</p></div>
          <dl><div><dt>Annunci attivi</dt><dd>{numFmt.format(totalActive)}</dd></div><div><dt>Reach totale</dt><dd>{numFmt.format(rows.reduce((sum, page) => sum + page.totalReach, 0))}</dd></div><div><dt>Brand</dt><dd>{rows.length}</dd></div></dl>
        </section>

        <section className="report-comparison report-section">
          <h2>Confronto diretto</h2>
          <div className="comparison-table" role="table" aria-label="Confronto metriche competitor">
            <div className="comparison-row comparison-head" role="row"><span role="columnheader">Competitor</span><span role="columnheader">Attivi</span><span role="columnheader">Reach</span><span role="columnheader">Durata</span><span role="columnheader">Quota</span></div>
            {[...rows].sort((a, b) => b.totalReach - a.totalReach).map((page) => <div className="comparison-row" role="row" key={page.pageId}><strong role="cell">{page.pageName ?? page.pageId}</strong><span role="cell" data-label="Attivi">{page.activeCount}</span><span role="cell" data-label="Reach">{numFmt.format(page.totalReach)}</span><span role="cell" data-label="Durata">{page.avgDays ? `${Math.round(page.avgDays)}gg` : 'n/d'}</span><span role="cell" data-label="Quota">{page.shareOfCategory != null ? `${page.shareOfCategory}%` : 'n/d'}</span></div>)}
          </div>
        </section>
      </>}

      {rows.map((p, i) => {
        const adsResult = topAds[ids.indexOf(p.pageId)]?.data;
        const demo = demos[ids.indexOf(p.pageId)]?.data ?? [];
        const totalDemo = demo.reduce((s, b) => s + b.male + b.female + b.unknown, 0);
        const female = demo.reduce((s, b) => s + b.female, 0);
        const male = demo.reduce((s, b) => s + b.male, 0);
        const spend = formatSpend(estimateSpend(p.totalReach, p.category));
        return (
          <section key={p.pageId} className="report-section">
            <h2>
              {i + 1}. {p.pageName}
            </h2>
            <dl className="report-grid">
              <div>
                <dt>Annunci in archivio</dt>
                <dd>
                  {p.adsCount} ({p.activeCount} attivi ora)
                </dd>
              </div>
              <div>
                <dt>Reach EU totale</dt>
                <dd>{numFmt.format(p.totalReach)}</dd>
              </div>
              {spend && (
                <div>
                  <dt>Spesa stimata</dt>
                  <dd>{spend}</dd>
                </div>
              )}
              {p.avgDays != null && p.avgDays > 0 && (
                <div>
                  <dt>Durata media annunci</dt>
                  <dd>{Math.round(p.avgDays)} giorni</dd>
                </div>
              )}
              {p.shareOfCategory != null && (
                <div>
                  <dt>Share categoria ({categoryLabel(p.category)})</dt>
                  <dd>{p.shareOfCategory}%</dd>
                </div>
              )}
              {p.rating != null && (
                <div>
                  <dt>Reputazione Google</dt>
                  <dd>
                    ★ {p.rating} ({numFmt.format(p.reviewsCount ?? 0)} recensioni)
                  </dd>
                </div>
              )}
              {p.pagespeedScore != null && (
                <div>
                  <dt>Velocità sito (mobile)</dt>
                  <dd>{p.pagespeedScore}/100</dd>
                </div>
              )}
              {totalDemo > 0 && (
                <div>
                  <dt>Pubblico reale</dt>
                  <dd>
                    {Math.round((female / (female + male || 1)) * 100)}% donne,{' '}
                    {Math.round((male / (female + male || 1)) * 100)}% uomini
                  </dd>
                </div>
              )}
            </dl>

            {adsResult && adsResult.content.length > 0 && (
              <>
                <h3>Annunci con più reach</h3>
                {adsResult.content.map((ad: Ad) => (
                  <div key={ad.id} className="report-ad">
                    <p className="report-ad-meta">
                      reach {numFmt.format(ad.euTotalReach ?? 0)}
                      {!ad.adDeliveryStopTime && ' · attivo'}
                      {ad.deliveryDays != null && ad.deliveryDays > 0 && ` · ${ad.deliveryDays}gg in onda`}
                      {' · '}
                      <a href={adLibraryUrl(ad.metaAdArchiveId)} target="_blank" rel="noopener noreferrer">
                        Ad Library
                      </a>
                    </p>
                    <blockquote>
                      {(ad.adCreativeBodies ?? '').split('\n---\n')[0].slice(0, 400) ||
                        '(annuncio solo immagine/video)'}
                    </blockquote>
                  </div>
                ))}
              </>
            )}
          </section>
        );
      })}

      <footer className="report-foot">
        <p>
          Reach e targeting: dati reali di trasparenza UE (Meta Ad Library). Spesa: stima da
          reach × CPM di settore. Reputazione: Google Maps. Generato con Osservio.
        </p>
      </footer>
    </div>
  );
}
